import { Fragment } from 'react';
import { Button, Divider, Upload } from 'antd';
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  ColumnWidthOutlined,
  DeleteOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ImageAlign } from './image-node';
import { useIsMobile } from '../../hooks/useIsMobile';
import { withTip } from './ToolbarButton';

const ALIGN_BTNS: { val: ImageAlign; icon: React.ReactNode; tip: string }[] = [
  { val: 'left', icon: <AlignLeftOutlined />, tip: '靠左' },
  { val: 'center', icon: <AlignCenterOutlined />, tip: '置中' },
  { val: 'right', icon: <AlignRightOutlined />, tip: '靠右' },
  { val: 'full', icon: <ColumnWidthOutlined />, tip: '滿版' },
];

interface Props {
  align: ImageAlign;
  onAlign: (a: ImageAlign) => void;
  onDelete: () => void;
  /** 從媒體庫挑一張替換這張圖（沒有提供就不顯示）。 */
  onReplaceFromLibrary?: () => void;
  /** 上傳新檔替換這張圖（沒有提供就不顯示）。 */
  onReplaceUpload?: (file: File) => void;
}

/**
 * 圖片選取時的浮動工具列：對齊四選一＋換圖（媒體庫／上傳）＋刪除。
 * 手機：改為排在圖片上方（不浮在圖上），窄圖／矮圖也不會蓋住圖說輸入框；按鈕 32px、無 Tooltip。
 */
export default function ImageToolbar({ align, onAlign, onDelete, onReplaceFromLibrary, onReplaceUpload }: Props) {
  const mobile = useIsMobile();
  const size = mobile ? 'middle' : 'small';
  const tip = (title: string, node: React.ReactElement) => withTip(mobile, title, node);
  // 桌機：外層撐滿圖片區塊寬度、內層 max-w-full + flex-wrap 置中浮在圖上
  const outer = mobile
    ? 'mb-1 flex justify-center'
    : 'pointer-events-none absolute inset-x-2 top-2 z-10 flex justify-center';
  return (
    <div className={outer}>
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-0.5 rounded-md bg-[#1f2733] px-1 py-1 shadow-lg">
        {ALIGN_BTNS.map((b) => (
          <Fragment key={b.val}>
            {tip(
              b.tip,
              <Button
                size={size}
                type={align === b.val ? 'primary' : 'text'}
                aria-label={b.tip}
                icon={b.icon}
                className={align === b.val ? '' : '!text-white'}
                onClick={() => onAlign(b.val)}
              />,
            )}
          </Fragment>
        ))}
        <Divider type="vertical" className="!mx-1 !border-white/25" />
        {onReplaceFromLibrary &&
          tip(
            '從媒體庫挑一張照片，取代這張（對齊與圖說保留）',
            <Button
              size={size}
              type="text"
              aria-label="換圖（媒體庫）"
              icon={<SwapOutlined />}
              className="!text-white"
              onClick={onReplaceFromLibrary}
            >
              {/* 手機只留圖示（aria-label 仍有說明），避免工具列換成多行 */}
              <span className="hidden sm:inline">換圖（媒體庫）</span>
            </Button>,
          )}
        {onReplaceUpload && (
          <Upload
            showUploadList={false}
            accept="image/*"
            beforeUpload={(file) => {
              onReplaceUpload(file as File);
              return false;
            }}
          >
            {tip(
              '上傳新圖片，取代這張（對齊與圖說保留）',
              <Button size={size} type="text" aria-label="上傳替換" icon={<UploadOutlined />} className="!text-white">
                <span className="hidden sm:inline">上傳替換</span>
              </Button>,
            )}
          </Upload>
        )}
        {tip(
          '刪除圖片',
          <Button size={size} type="text" danger aria-label="刪除圖片" icon={<DeleteOutlined />} onClick={onDelete} />,
        )}
      </div>
    </div>
  );
}
